// Scene 1 - Landschaften + Werke.
// Portiert aus Mock: renderLandscape, renderWerkList, drawFan, updateFanState.
// Die Bezier-Fanlinien werden nach dem Rendering von QAS-Anker zu Werk-Karten
// gezeichnet und laufen selbstzeichnend ein.

import React, { useMemo, useRef, useState } from "react";
import { useLang } from "../i18n";
import { useTransportV2, hasWerke, allWerke, activeWerke } from "../context/TransportV2Context";
import { Button, MenuItem, Select } from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { readPlantSort, rememberPlantSort, sortPlants } from "../utils/plantSortPreferences";
import { Cx } from "../components/IconSprite";
import { usePlantConnections } from "../hooks/usePlantConnections";
import { usePipelineReady } from "../hooks/usePipelineReady";
import { transportTargetIndex } from "../utils/transportTarget";
import StageTargetList from "../components/StageTargetList";

// `targetSource` (optional, Phase B): `{ [stage]: { [targetId]: kennung[] } }` für den Hinweis „durch Werk … gesetzt“.
export default function Scene1Landscape({ userId, targetSource: targetSourceProp = null }) {
  const { t, lang } = useLang();
  const { state, dispatch } = useTransportV2();
  // Ohne Mehrzielmodus und Katalog ist plantTargetSource leer; dann bleibt das DOM unverändert.
  const targetSource = targetSourceProp || state.plantTargetSource || null;
  const pipe = usePipelineReady(state.spaceId);
  const sapModus = pipe.modus === "sap";
  const space = state.space;
  const [sort, setSort] = useState(() => readPlantSort(userId));
  const werkeList = useMemo(() => sortPlants(allWerke(state), sort, lang), [state, sort, lang]);
  const activeCount = useMemo(() => activeWerke(state).length, [state]);
  const fanBoxRef = useRef(null);
  const fanSvgRef = useRef(null);
  const fanAnchorRef = useRef(null);
  const werkListRef = useRef(null);

  const togglePlant = (key) => {
    if (!werkeList.some(plant => plant.key === key)) return;
    dispatch({ type: "TOGGLE_WERK", payload: key });
  };

  const plantNames = useMemo(() => targetSource ? Object.fromEntries(werkeList.map(w => [w.kennung, w.name])) : null, [targetSource, werkeList]);

  const connectionScope = JSON.stringify([state.spaceId, state.sourceInstanceId, space?.qasInstance?.id, space?.zielStufe]);
  usePlantConnections({
    scope: connectionScope, plantKeys: JSON.stringify(werkeList.map(w => w.key)),
    enabled: !!space && transportTargetIndex(space) >= 0 && hasWerke(state), selection: state.werkOn,
    boxRef: fanBoxRef, svgRef: fanSvgRef, anchorRef: fanAnchorRef, listRef: werkListRef,
  });

  if (!space) return null;

  const stages = space.stages || [];
  const tgtIdx = transportTargetIndex(space);
  const src = stages[0];
  const tgt = stages[tgtIdx];
  // Mehrzielmodus: Stufen ab dem Ziel mit mehreren Zielen zeigen eine Zielliste statt der Instanz.
  const stageRows = (st, i) => state.multiTarget && i >= tgtIdx && st.stageKey !== "dev" && state.stageTargets?.[st.stageKey]?.length > 1 ? state.stageTargets[st.stageKey] : null;
  const meta = t("s1.meta", { name: space.name || "", n: stages.length }) + (hasWerke(state) ? "" : " · " + t("s3.single"));

  return (
    <section className="scene active scene-enter" id="s1">
      <div className="sh">
        <h2>{t("s1.h2")}</h2>
        <p>{t("s1.p")}</p>
      </div>

      <div className="panel">
        <div className="panel-h">
          <h3>{t("s1.landTitle")}</h3>
          <span className="meta">{meta}</span>
        </div>
        <div className="land-row">
          {stages.map((st, i) => {
            const mod = i === 0 ? "stagecard--src" : i === tgtIdx ? "stagecard--tgt" : i === stages.length - 1 ? "stagecard--dim" : "";
            const role = i === 0 ? t("role.src") : i === tgtIdx ? t("role.tgt") : i === stages.length - 1 ? t("role.rel") : t("role.pipe");
            return (
              <React.Fragment key={"stg" + i}>
                {i > 0 && <div className="land-sep"><Cx /></div>}
                <div className={"stagecard " + mod + " stagger-in"} style={{ "--i": i }}>
                  <div className="row1">
                    <span className="gl"><svg className="ic"><use href={"#" + (st.glyph || "ic-stage-dev")} /></svg></span>
                    <span className="code">{st.code}</span>
                    <span className="role">{role}</span>
                  </div>
                  {stageRows(st, i) ? (
                    <StageTargetList stageKey={st.stageKey} code={st.code} rows={stageRows(st, i)} selected={state.stageTargetSel?.[st.stageKey] || []}
                      lockedId={i === tgtIdx ? (stageRows(st, i).find(row => row.isDefault) || stageRows(st, i)[0]).targetId : null}
                      disabled={!!state.batch || !!state.batchRequest} onToggle={(stage, targetId) => dispatch({ type: "TOGGLE_STAGE_TARGET", payload: { stage, targetId } })}
                      {...(targetSource?.[st.stageKey] ? { source: targetSource[st.stageKey], plantNames } : {})} />
                  ) : <div className="inst">{st.inst}</div>}
                  <div className="note">{st.name}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        {src && tgt && <div className="land-info">
          <svg className="ic ic-16"><use href="#ic-info" /></svg>
          <span>{t(sapModus ? "s1.infoSap" : "s1.info", { src: src.code, tgt: tgt.code, cx: <Cx /> })}</span>
        </div>}
      </div>

      {tgt && hasWerke(state) && (
        <div className="sect">
          <div className="panel">
            <div className="panel-h">
              <h3>{t("s1.werkTitle")}</h3>
              <span className="meta">
                {activeCount
                  ? t("s1.werkMeta", { x: activeCount, n: werkeList.length })
                  : t("s1.werkNone")}
              </span>
            </div>
            <div className="plant-controls">
              <Button variant="outlined" className="plant-select-all" disabled={!!state.batch || !!state.batchRequest} onClick={() => dispatch({ type: "SET_ALL_WERKE", payload: activeCount !== werkeList.length })}
                sx={{ minHeight: 44, textTransform: "none", fontFamily: "inherit", fontWeight: 600, color: "var(--t1)", borderColor: "var(--border)" }}>
                {t(activeCount === werkeList.length ? "s1.clearPlants" : "s1.allPlants")}
              </Button>
              <Select value={sort} onChange={event => { setSort(event.target.value); rememberPlantSort(userId, event.target.value); }}
                inputProps={{ "aria-label": t("s1.sort") }} IconComponent={ExpandMoreRounded}
                sx={{ height: 44, fontFamily: "inherit", fontSize: 14, color: "var(--t1)", borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border)" }, "& .MuiSelect-icon": { color: "var(--t3)" } }}
                MenuProps={{ PaperProps: { className: "iio", sx: { backgroundColor: "var(--iio-bg-1)", color: "var(--iio-text-1)", backgroundImage: "none", maxWidth: "calc(100vw - 32px)" } } }}>
                {["number", "name"].map(value => <MenuItem key={value} value={value}
                  sx={{ fontFamily: '"Source Sans 3", sans-serif', fontSize: 14, minHeight: 44 }}>{t("s1.sort." + value)}</MenuItem>)}
              </Select>
            </div>
            <div className="fanbox" ref={fanBoxRef}>
              <svg className="fansvg" ref={fanSvgRef} aria-hidden="true" key={connectionScope}>
                {werkeList.map(w => <path key={w.key} data-fan={w.key} pathLength="1"
                  className={"pl-line fan-line" + (state.werkOn[w.key] ? "" : " off")}
                  style={{ stroke: "var(--signal)", strokeDasharray: 1, strokeDashoffset: 0 }} />)}
              </svg>
              <div className="fan-anchor" ref={fanAnchorRef}>
                <div className="stagecard stagecard--tgt" style={{ minWidth: 0 }}>
                  <div className="row1">
                    <span className="gl"><svg className="ic"><use href={"#" + tgt.glyph} /></svg></span>
                    <span className="code">{tgt.code}</span>
                    <span className="role">{t("role.tgt")}</span>
                  </div>
                  <div className="inst">{tgt?.inst || ""}</div>
                  <div className="note">{t("s1.qasNote")}</div>
                </div>
              </div>
              <div />
              <div className="werk-list" ref={werkListRef}>
                {werkeList.map((w, i) => {
                  const on = !!state.werkOn[w.key];
                  return (
                    <button
                      key={w.key || w.kennung}
                      type="button"
                      className={"werk-card" + (on ? " on" : "") + " stagger-in"}
                      style={{ "--i": i }}
                      data-werk={w.key}
                      aria-pressed={on}
                      disabled={!!state.batch || !!state.batchRequest}
                      onClick={() => togglePlant(w.key)}
                    >
                      <span className="wc-check">
                        <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12.5 L10 17.5 L19 6.5" />
                        </svg>
                      </span>
                      <span>
                        <span className="wc-name">{w.name}</span>
                        <span className="wc-sub" style={{ display: "block" }}>{t("s1.werkSub")}</span>
                      </span>
                      <span className="wc-ken">{w.kennung}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 0 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <button className="btn btn-cta" onClick={() => dispatch({ type: "SET_STEP", payload: 2 })}>
          {t("s1.next")}
          <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
        </button>
      </div>
    </section>
  );
}
